// Refer & Earn (web) — deliberately OFF the main navigation: reached from the
// avatar dropdown, Dana's deep link, or the direct URL. Mirrors the mobile
// experience: simplified user-safe statuses, per-currency reward buckets
// (never summed), idempotent submission via a per-form client key.
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Gift, Loader2, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  acceptReferralTerms, getMyReferralSummary, getReferralProgram, listMyReferrals, submitReferral,
  type MyReferral, type ReferralProgramStatus, type RewardBucket, type RewardSummary,
} from '@/lib/referralApi';

const STATUS_LABEL: Record<string, string> = {
  SUBMITTED: 'Submitted',
  UNDER_REVIEW: 'Under review',
  ACCEPTED: 'Accepted',
  IN_PROGRESS: 'In progress',
  DEMO_SCHEDULED: 'Demo scheduled',
  CUSTOMER_JOINED: 'Customer joined 🎉',
  ALREADY_A_CUSTOMER: 'Already a customer',
  NOT_ACCEPTED: 'Not accepted',
  CLOSED: 'Closed',
};

function chipCls(s: string): string {
  if (s === 'CUSTOMER_JOINED' || s === 'ACCEPTED')
    return 'bg-green-100 text-green-700 dark:bg-green-500/[0.14] dark:text-green-400';
  if (s === 'UNDER_REVIEW' || s === 'IN_PROGRESS' || s === 'DEMO_SCHEDULED')
    return 'bg-orange-100 text-orange-700 dark:bg-orange-500/[0.14] dark:text-orange-300';
  return 'bg-muted text-foreground';
}

const rewardLine = (b?: RewardBucket[]) =>
  !b || b.length === 0 ? '—' : b.map((x) => `${x.currency} ${Number(x.amount).toLocaleString()}`).join(' · ');

const uuidv4 = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });

const inputCls =
  'w-full h-9 px-3 rounded-lg border border-border bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-orange-300';

export const ReferEarnPage: React.FC = () => {
  const { t } = useTranslation('common');
  const [program, setProgram] = useState<ReferralProgramStatus | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [referrals, setReferrals] = useState<MyReferral[]>([]);
  const [rewards, setRewards] = useState<RewardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [clientKey, setClientKey] = useState('');
  const [f, setF] = useState<Record<string, string>>({ country: 'TZ' });

  const load = () => {
    setLoading(true);
    Promise.all([getReferralProgram(), listMyReferrals(), getMyReferralSummary()])
      .then(([p, r, s]) => {
        setProgram(p);
        setUnavailable(!p.enabled);
        setReferrals(r);
        setRewards(s.rewards);
      })
      .catch(() => setUnavailable(true))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openForm = () => {
    setClientKey(uuidv4());
    setF({ country: 'TZ' });
    setOpen(true);
  };

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setF((s) => ({ ...s, [k]: e.target.value }));

  const doSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (program && !program.terms_accepted) await acceptReferralTerms(f.country);
      const res = await submitReferral({
        client_key: clientKey,
        company_name: (f.company_name || '').trim(),
        country: f.country || 'TZ',
        industry: f.industry?.trim() || undefined,
        employee_count: f.employee_count ? Number(f.employee_count) : undefined,
        website: f.website?.trim() || undefined,
        contact_name: (f.contact_name || '').trim(),
        contact_title: f.contact_title?.trim() || undefined,
        contact_email: (f.contact_email || '').trim(),
        contact_phone: f.contact_phone?.trim() || undefined,
        relationship: f.relationship?.trim() || undefined,
        notes: f.notes?.trim() || undefined,
      });
      setOpen(false);
      toast.success(`Thanks — we received your referral of ${res.company_name}. We'll keep you posted.`, { duration: 6000 });
      load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not submit the referral.'), { duration: 6000 });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> {t('loading', 'Loading…')}
      </div>
    );
  }

  if (unavailable) {
    return (
      <div className="p-6 max-w-2xl">
        <Card>
          <CardContent className="py-10 text-center">
            <Gift className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
            <p className="font-medium text-foreground">Refer & Earn isn't available</p>
            <p className="text-sm text-muted-foreground mt-1">Referrals aren't enabled for your organization right now.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Gift className="w-6 h-6 text-orange-500" /> Refer &amp; Earn
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Know a company that could benefit from KaziPeople? Introduce them and earn a reward when they become an
            eligible paying customer.
          </p>
        </div>
        <Button onClick={openForm} className="bg-orange-600 hover:bg-orange-700 text-white shrink-0">
          <Plus className="w-4 h-4 mr-1.5" /> Refer a business
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Rewards</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div><p className="text-muted-foreground">Pending</p><p className="font-semibold text-foreground">{rewardLine(rewards?.pending)}</p></div>
            <div><p className="text-muted-foreground">Earned</p><p className="font-semibold text-foreground">{rewardLine(rewards?.earned)}</p></div>
            <div><p className="text-muted-foreground">Paid</p><p className="font-semibold text-foreground">{rewardLine(rewards?.paid)}</p></div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">My referrals</CardTitle></CardHeader>
        <CardContent>
          {referrals.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No referrals yet. Know a business that could benefit from KaziPeople? Introduce them and track it here.
            </p>
          ) : (
            <div className="divide-y divide-border">
              {referrals.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-3 py-2.5">
                  <div className="min-w-0">
                    <p className="font-medium text-foreground truncate">{r.company_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(r.created_at).toLocaleDateString()} · {r.country}
                    </p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${chipCls(r.status)}`}>
                    {STATUS_LABEL[r.status] || 'Submitted'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !busy && setOpen(false)}>
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-background border border-border p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-foreground">Refer a business</h3>
            <form onSubmit={doSubmit} className="mt-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="col-span-2 text-sm font-medium text-foreground">Company name *
                  <input required className={`${inputCls} mt-1`} value={f.company_name || ''} onChange={set('company_name')} />
                </label>
                <label className="text-sm font-medium text-foreground">Country *
                  <select className={`${inputCls} mt-1`} value={f.country} onChange={set('country')}>
                    {['TZ', 'KE', 'RW', 'UG', 'BI', 'CD', 'ZM', 'MW'].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
                <label className="text-sm font-medium text-foreground">Industry
                  <input className={`${inputCls} mt-1`} value={f.industry || ''} onChange={set('industry')} />
                </label>
                <label className="text-sm font-medium text-foreground">Approx. employees
                  <input type="number" min="1" className={`${inputCls} mt-1`} value={f.employee_count || ''} onChange={set('employee_count')} />
                </label>
                <label className="text-sm font-medium text-foreground">Website
                  <input className={`${inputCls} mt-1`} value={f.website || ''} onChange={set('website')} />
                </label>
                <label className="text-sm font-medium text-foreground">Contact name *
                  <input required className={`${inputCls} mt-1`} value={f.contact_name || ''} onChange={set('contact_name')} />
                </label>
                <label className="text-sm font-medium text-foreground">Contact title
                  <input className={`${inputCls} mt-1`} value={f.contact_title || ''} onChange={set('contact_title')} />
                </label>
                <label className="text-sm font-medium text-foreground">Contact email *
                  <input type="email" required className={`${inputCls} mt-1`} value={f.contact_email || ''} onChange={set('contact_email')} />
                </label>
                <label className="text-sm font-medium text-foreground">Contact phone
                  <input className={`${inputCls} mt-1`} value={f.contact_phone || ''} onChange={set('contact_phone')} />
                </label>
                <label className="col-span-2 text-sm font-medium text-foreground">How do you know them?
                  <input className={`${inputCls} mt-1`} value={f.relationship || ''} onChange={set('relationship')} />
                </label>
                <label className="col-span-2 text-sm font-medium text-foreground">Notes
                  <textarea rows={3} className={`${inputCls} mt-1 h-auto py-2`} value={f.notes || ''} onChange={set('notes')} />
                </label>
              </div>
              {program && !program.terms_accepted && (
                <p className="text-xs text-muted-foreground">
                  By submitting you accept the KaziPeople referral terms ({program.terms_version}).
                </p>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
                <Button type="submit" disabled={busy} className="bg-orange-600 hover:bg-orange-700 text-white">
                  {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Submit referral
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferEarnPage;
